import React from "react";
import { Paper, Text, Stack, Badge, Progress, Group } from "@mantine/core";

interface Props {
  batchData: any; // Batch data from getBatchDetails (source of truth for MOU)
  trainerEmail?: string; // Override for current trainer (e.g. from latest journey)
}

const toNum = (v: any) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const formatHours = (hrs: number) => {
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return m === 60 ? `${h + 1}h 0m` : `${h}h ${m}m`;
};

/**
 * Resolve the current trainer from the batch instructors.
 * Priority: batch.trainerEmail → passed trainerEmail → latest dateOfAssign.
 */
export const getCurrentInstructor = (batchData: any, trainerEmail?: string) => {
  const instructors: any[] = batchData?.instructors || [];
  if (instructors.length === 0) return null;

  const emails = [batchData?.trainerEmail, trainerEmail]
    .filter(Boolean)
    .map((e: string) => e.toLowerCase().trim());

  for (const email of emails) {
    const match = instructors.find(
      (i) => (i.email || "").toLowerCase().trim() === email,
    );
    if (match) return match;
  }

  return [...instructors].sort(
    (a, b) =>
      new Date(b.dateOfAssign || 0).getTime() -
      new Date(a.dateOfAssign || 0).getTime(),
  )[0];
};

const MouHoursCard = ({ batchData, trainerEmail }: Props) => {
  const current = getCurrentInstructor(batchData, trainerEmail);
  const others: any[] = (batchData?.instructors || []).filter(
    (i: any) => i !== current,
  );

  if (!current) {
    return (
      <Paper className="p-3 border border-[#FC7A69]/20 bg-white/50" radius="md">
        <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
          MOU Hours
        </Text>
        <Text className="text-sm text-[#221E45]/50">N/A</Text>
      </Paper>
    );
  }

  const mouHours = toNum(current.mouHours);
  const attended = toNum(current.attendedHours);
  const remaining = Math.max(mouHours - attended, 0);
  const exceeded = mouHours > 0 && attended > mouHours;
  const percent = mouHours > 0 ? Math.min((attended / mouHours) * 100, 100) : 0;
  const color = exceeded ? "red" : percent >= 80 ? "orange" : "teal";

  return (
    <Paper className="p-3 border border-[#FC7A69]/20 bg-white/50" radius="md">
      <Group justify="space-between" mb={4} wrap="nowrap">
        <Text className="text-xs text-[#221E45]/60 font-medium">MOU Hours</Text>
        {exceeded && (
          <Badge size="xs" color="red" variant="light">
            Exceeded
          </Badge>
        )}
      </Group>

      <Stack gap={4}>
        <Text className="text-sm">
          <span className={`font-bold ${exceeded ? "text-red-500" : "text-[#FC7A69]"}`}>
            {attended.toFixed(2).replace(/\.00$/, "")}
          </span>
          <span className="text-[#221E45]/60">
            /{mouHours || "—"} hrs
          </span>
        </Text>

        {mouHours > 0 && <Progress value={percent} color={color} size="sm" radius="xl" />}

        <Text className="text-xs text-[#221E45]/50">
          {mouHours > 0
            ? exceeded
              ? `Over by ${formatHours(attended - mouHours)}`
              : `${formatHours(remaining)} remaining`
            : "MOU hours not set"}
        </Text>

        <Text className="text-xs font-bold text-[#221E45] truncate">
          {current.name}
        </Text>

        {others.length > 0 && (
          <Stack gap={0} mt={2}>
            {others.map((ins: any, idx: number) => (
              <Text key={idx} size="xs" c="dimmed" className="truncate">
                Prev: {ins.name} – {toNum(ins.attendedHours)}/
                {toNum(ins.mouHours) || "—"} hrs
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default MouHoursCard;
